import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import FormModal from '../../Modals/FormModal';
import RecordTable from '../../Tables/record/RecordTable';
import PurchaseInfoCard from '../common/PurchaseInfoCard';
import PaymentConditionSection from '../common/PaymentConditionSection';
import SvgIcon from '../../SvgIcon/SvgIcon';
import { useSupplier } from '../../../hooks/useSupplier';
import { useProduct } from '../../../hooks/useProduct';
import AddPriceAdjustmentForm from './AddPriceAdjustmentForm';
import { debitNoteProductColumns } from '../../../features/purchases/debitNote/debitNoteProductColumns';
import { useDebitNote } from '../../../hooks/purchases/useDebitNote';
import AddDebitServiceForm from './AddDebitServiceForm';
import AddDebitNoteProductForm from './AddDebitNoteProductForm';
import { DebitNoteProduct } from '../../../types/purchases/DebitNotePurchase';
import ProductSettlementModal from '../settlement/ProductSettlementModal';
import GeneralProductSettlementModal from '../settlement/GeneralProductSettlementModal';
import { useProductSettlement } from '../../../hooks/settlement/useProductSettlement';

const DebitNoteSection = () => {
  const {
    openAddServiceModal,
    closeAddServiceModal,
    showAddServiceModal,
    openAddProductModal,
    closeAddProductModal,
    showAddProductModal,
    openPriceAdjustmentModal,
    closePriceAdjustmentModal,
    showPriceAdjustmentModal,
    handlerAddProduct,
    handlerDeleteProduct,
  } = useDebitNote();

  const { form, purchaseSettlementLoaded } = useSelector(
    (state: RootState) => state.basePurchase
  );

  const { suppliersDropdown, handlerGetSuppliersForDropdown } = useSupplier();
  const { handlerGetProductsForDropdown, productsDropdown } = useProduct();

  useEffect(() => {
    handlerGetSuppliersForDropdown();
    handlerGetProductsForDropdown();
  }, []);

  const columns = useMemo(() => debitNoteProductColumns, []);

  const {
    handlerGetSettlementsByProduct,
    showGeneralSettlementModal,
    showSettlementModal,
    handlerOpenGeneralModal,
    handlerCloseGeneralModal,
    handlerCloseSettlementModal,
  } = useProductSettlement();

  const actions = useMemo(() => {
    if (purchaseSettlementLoaded) {
      return [
        {
          key: 'settle',
          onClick: (row: DebitNoteProduct) => {
            handlerGetSettlementsByProduct(form.id, row.id);
          },
        },
      ];
    }

    return [
      {
        key: 'delete',
        onClick: (row: DebitNoteProduct) => {
          handlerDeleteProduct(row.id);
        },
      },
    ];
  }, [purchaseSettlementLoaded, form.id]);

  return (
    <div>
      <PurchaseInfoCard
        suppliers={suppliersDropdown}
        title="Compra de Nota de Débito"
        maxWidth="w-full"
      />

      <RecordTable
        maxWidth="w-full"
        columns={columns}
        data={form.products}
        actions={actions}
        noDataMessage="No hay productos agregados."
        tableHeaderContent={
          <h3 className="text-lg font-medium">
            Productos en la Nota de Débito
          </h3>
        }
        tableHeaderActions={
          purchaseSettlementLoaded ? (
            <button
              onClick={handlerOpenGeneralModal}
              className="flex items-center gap-2 rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90"
            >
              <SvgIcon name="settle-products" />
              Asentar General
            </button>
          ) : (
            <>
              <button
                onClick={openPriceAdjustmentModal}
                className="flex items-center gap-2 rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90"
              >
                <SvgIcon name="configuration" />
                Ajustar Precio del Producto
              </button>

              <button
                onClick={openAddProductModal}
                className="flex items-center gap-2 rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90"
              >
                <SvgIcon name="addCircle" />
                Agregar Producto
              </button>

              <button
                onClick={openAddServiceModal}
                className="flex items-center gap-2 rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90"
              >
                <SvgIcon name="serviceAdd" />
                Agregar Servicio
              </button>
            </>
          )
        }
      />

      {/* Modal Agregar Producto */}
      <FormModal
        isOpen={showAddProductModal}
        onClose={closeAddProductModal}
        title="Agregar Producto"
        maxWidth="max-w-4xl"
      >
        <AddDebitNoteProductForm
          onAdd={handlerAddProduct}
          productOptions={productsDropdown}
        />
      </FormModal>

      {/* Modal Ajustar Precio del Producto */}
      <FormModal
        isOpen={showPriceAdjustmentModal}
        onClose={closePriceAdjustmentModal}
        title="Ajustar Precio del Producto"
        maxWidth="max-w-4xl"
      >
        <AddPriceAdjustmentForm onAdd={handlerAddProduct} />
      </FormModal>

      {/* Modal Agregar Servicio */}
      <FormModal
        isOpen={showAddServiceModal}
        onClose={closeAddServiceModal}
        title="Agregar Servicio"
        maxWidth="max-w-4xl"
      >
        <AddDebitServiceForm onAdd={handlerAddProduct} />
      </FormModal>

      <PaymentConditionSection />

      <ProductSettlementModal
        isOpen={showSettlementModal}
        onClose={handlerCloseSettlementModal}
      />

      <GeneralProductSettlementModal
        isOpen={showGeneralSettlementModal}
        onClose={handlerCloseGeneralModal}
      />
    </div>
  );
};

export default DebitNoteSection;
